import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiGithub,
  FiTwitter,
  FiDownload,
} from 'react-icons/fi';
import {
  FaLinkedin,
  FaYoutube,
  FaFacebook,
  FaReact,
  FaNodeJs,
  FaPython,
  FaDatabase,
} from 'react-icons/fa';
import {
  SiTypescript,
  SiTailwindcss,
  SiNextdotjs,
  SiMongodb,
} from 'react-icons/si';
import TypewriterText from './TypewriterText';
import MyCv from './MyCv';

function Icon(component: any, className?: string) {
  return React.createElement(component as React.ComponentType<{ className?: string }>, { className });
}

/* ─── Data ──────────────────────────────────────────────── */

const socialLinks = [
  { icon: FiGithub, url: 'https://github.com/evodenuby', label: 'GitHub', color: 'hover:text-textPrimary' },
  { icon: FaLinkedin, url: 'https://www.linkedin.com/in/evode-muyisingizemwese', label: 'LinkedIn', color: 'hover:text-[#0A66C2]' },
  { icon: FiTwitter, url: 'https://twitter.com/@evodeSTACK', label: 'Twitter', color: 'hover:text-[#1DA1F2]' },
  { icon: FaYoutube, url: 'https://youtube.com/@evodenuby', label: 'YouTube', color: 'hover:text-[#FF0000]' },
  { icon: FaFacebook, url: 'https://facebook.com/@evodeSTACK', label: 'Facebook', color: 'hover:text-[#1877F2]' },
];

const stats = [
  { number: '7+', label: 'Projects' },
  { number: '3+', label: 'Years Exp.' },
  { number: '95%', label: 'Satisfaction' },
];

const floatingIcons = [
  { Icon: FaReact, className: 'text-[#61DAFB]', size: 28, x: -60, y: -120, delay: 0 },
  { Icon: SiTypescript, className: 'text-[#3178C6]', size: 22, x: 80, y: -90, delay: 0.4 },
  { Icon: FaNodeJs, className: 'text-[#339933]', size: 24, x: -80, y: 60, delay: 0.8 },
  { Icon: SiTailwindcss, className: 'text-[#06B6D4]', size: 22, x: 90, y: 40, delay: 1.2 },
  { Icon: FaPython, className: 'text-[#FFD43B]', size: 20, x: -40, y: 130, delay: 0.6 },
  { Icon: SiMongodb, className: 'text-[#47A248]', size: 20, x: 60, y: 120, delay: 1.0 },
  { Icon: FaDatabase, className: 'text-secondary', size: 18, x: -100, y: -20, delay: 1.4 },
  { Icon: SiNextdotjs, className: 'text-textPrimary', size: 22, x: 100, y: -20, delay: 0.2 },
];

/* ─── Animation Variants ────────────────────────────────── */

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

const itemVariants = {
  hidden: { y: 28, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

/* ─── SVG Background ────────────────────────────────────── */

const SvgBackground: React.FC = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {/* Base SVG canvas */}
    <svg
      className="absolute inset-0 w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
    >
      <defs>
        {/* Dot grid pattern */}
        <pattern id="hero-dots" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.8" fill="var(--color-border)" opacity="0.4" />
        </pattern>

        {/* Radial gradient for depth */}
        <radialGradient id="hero-glow" cx="30%" cy="40%" r="60%">
          <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.08" />
          <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
        </radialGradient>

        <radialGradient id="hero-glow-2" cx="70%" cy="70%" r="50%">
          <stop offset="0%" stopColor="#a855f7" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Dot grid */}
      <rect width="100%" height="100%" fill="url(#hero-dots)" />

      {/* Ambient glows */}
      <rect width="100%" height="100%" fill="url(#hero-glow)" />
      <rect width="100%" height="100%" fill="url(#hero-glow-2)" />

      {/* Floating geometric particles */}
      <g className="svg-particle-1">
        <circle cx="15%" cy="25%" r="2" fill="var(--color-accent)" opacity="0.25" />
      </g>
      <g className="svg-particle-2">
        <rect x="75%" y="15%" width="6" height="6" rx="1" fill="var(--color-accent)" opacity="0.15" transform="rotate(45 78 18)" />
      </g>
      <g className="svg-particle-3">
        <circle cx="85%" cy="60%" r="1.5" fill="#a855f7" opacity="0.2" />
      </g>
      <g className="svg-particle-4">
        <polygon points="25%,70% 27%,66% 29%,70%" fill="var(--color-accent)" opacity="0.15" />
      </g>
      <g className="svg-particle-5">
        <circle cx="50%" cy="80%" r="2.5" fill="#06b6d4" opacity="0.15" />
      </g>
      <g className="svg-particle-6">
        <rect x="40%" y="20%" width="4" height="4" rx="0.5" fill="var(--color-accent)" opacity="0.12" transform="rotate(30 42 22)" />
      </g>
      <g className="svg-particle-7">
        <circle cx="65%" cy="45%" r="1.8" fill="#a855f7" opacity="0.18" />
      </g>
      <g className="svg-particle-8">
        <circle cx="10%" cy="55%" r="1.2" fill="#06b6d4" opacity="0.2" />
      </g>

      {/* Floating code symbols */}
      <text x="20%" y="35%" className="svg-code-symbol-1" fill="var(--color-accent)" opacity="0.08" fontSize="24" fontFamily="JetBrains Mono, monospace">{'{ }'}</text>
      <text x="70%" y="25%" className="svg-code-symbol-2" fill="var(--color-accent)" opacity="0.06" fontSize="18" fontFamily="JetBrains Mono, monospace">{'< />'}</text>
      <text x="30%" y="85%" className="svg-code-symbol-1" fill="var(--color-accent)" opacity="0.05" fontSize="14" fontFamily="JetBrains Mono, monospace">{'[ ]'}</text>
    </svg>
  </div>
);

/* ─── Hero Component ────────────────────────────────────── */

const Hero: React.FC = () => {
  const [isCvOpen, setIsCvOpen] = useState(false);

  return (
    <>
      {/* Inject keyframes for SVG animations */}
      <style>{`
        @keyframes svgFloat1 { 0%,100%{ transform:translate(0,0) } 50%{ transform:translate(8px,-12px) } }
        @keyframes svgFloat2 { 0%,100%{ transform:translate(0,0) } 50%{ transform:translate(-10px,-8px) } }
        @keyframes svgFloat3 { 0%,100%{ transform:translate(0,0) } 50%{ transform:translate(6px,10px) } }
        @keyframes svgFloat4 { 0%,100%{ transform:translate(0,0) } 50%{ transform:translate(-8px,6px) } }
        @keyframes svgSymbolDrift { 0%,100%{ transform:translateY(0); opacity:inherit } 50%{ transform:translateY(-8px); opacity:0.04 } }
        @keyframes ringPulse { 0%,100%{ box-shadow: 0 0 20px 2px var(--color-accent) } 50%{ box-shadow: 0 0 36px 6px var(--color-accent) } }
        @keyframes floatY { 0%,100%{ transform:translateY(0) } 50%{ transform:translateY(-6px) } }
        @keyframes orbFloat { 0%,100%{ transform:translate(0,0) scale(1) } 33%{ transform:translate(12px,-8px) scale(1.05) } 66%{ transform:translate(-6px,6px) scale(0.97) } }

        .svg-particle-1 { animation: svgFloat1 8s ease-in-out infinite; }
        .svg-particle-2 { animation: svgFloat2 10s ease-in-out infinite 1s; }
        .svg-particle-3 { animation: svgFloat3 9s ease-in-out infinite 0.5s; }
        .svg-particle-4 { animation: svgFloat4 11s ease-in-out infinite 2s; }
        .svg-particle-5 { animation: svgFloat1 7s ease-in-out infinite 1.5s; }
        .svg-particle-6 { animation: svgFloat2 12s ease-in-out infinite 0.8s; }
        .svg-particle-7 { animation: svgFloat3 8.5s ease-in-out infinite 3s; }
        .svg-particle-8 { animation: svgFloat4 9.5s ease-in-out infinite 2.5s; }

        .svg-code-symbol-1 { animation: svgSymbolDrift 10s ease-in-out infinite; }
        .svg-code-symbol-2 { animation: svgSymbolDrift 12s ease-in-out infinite 3s; }

        .hero-orb-1 { animation: orbFloat 14s ease-in-out infinite; }
        .hero-orb-2 { animation: orbFloat 18s ease-in-out infinite 4s; }

        .profile-ring { animation: ringPulse 3s ease-in-out infinite; }

        .floating-icon { animation: floatY 4s ease-in-out infinite; }
      `}</style>

      <section id="home" className="relative min-h-screen bg-primary overflow-hidden">
        {/* ── SVG Background ── */}
        <SvgBackground />

        {/* ── Ambient Gradient Orbs ── */}
        <div className="hero-orb-1 absolute top-1/4 left-1/4 w-72 h-72 bg-secondary/5 rounded-full blur-[120px]" />
        <div className="hero-orb-2 absolute bottom-1/4 right-1/3 w-96 h-96 bg-purple-500/5 rounded-full blur-[140px]" />

        <div className="flex flex-col lg:flex-row min-h-screen">
          {/* ═══════════════ LEFT: Content ═══════════════ */}
          <div className="w-full lg:w-1/2 relative flex items-center">
            <div className="container mx-auto px-6 sm:px-8 lg:px-12 xl:px-16 py-24 relative z-10">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {/* Heading */}
                <motion.h1
                  variants={itemVariants}
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-textPrimary leading-[1.1]"
                >
                  Hello,
                  <br />
                  I am{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
                    Evode
                  </span>
                </motion.h1>

                {/* Typewriter in glass card */}
                <motion.div variants={itemVariants} className="mt-5">
                  <div className="inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-primary/60 border border-border/40 backdrop-blur-md">
                    <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                    <TypewriterText />
                  </div>
                </motion.div>

                {/* Description */}
                <motion.p
                  variants={itemVariants}
                  className="text-textSecondary text-sm md:text-base mt-6 max-w-lg leading-relaxed"
                >
                  Full Stack Developer & Aspiring Data Scientist in Rwanda specializing in MERN Stack, React, Node.js, TypeScript & Python. 
                  Turning ideas into scalable web applications and intelligent solutions
                </motion.p>

                {/* Stats with dividers */}
                <motion.div
                  variants={itemVariants}
                  className="flex items-center gap-6 mt-8"
                >
                  {stats.map((s, i) => (
                    <React.Fragment key={s.label}>
                      <motion.div
                        whileHover={{ y: -2 }}
                        transition={{ duration: 0.2 }}
                        className="text-center"
                      >
                        <div className="text-xl font-bold text-textPrimary">
                          {s.number}
                        </div>
                        <div className="text-[11px] text-textSecondary/70 font-medium tracking-wide">
                          {s.label}
                        </div>
                      </motion.div>
                      {i < stats.length - 1 && (
                        <div className="w-px h-8 bg-border/50" />
                      )}
                    </React.Fragment>
                  ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                  variants={itemVariants}
                  className="flex flex-wrap gap-3 mt-8"
                >
                  <motion.a
                    href="#projects"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    className="group px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg text-xs font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-secondary/30"
                  >
                    View My Work
                  </motion.a>

                  <motion.button
                    onClick={() => setIsCvOpen(true)}
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    className="group relative px-5 py-2.5 border-2 border-textPrimary/20 dark:border-border/60 rounded-lg text-xs font-medium text-textPrimary overflow-hidden transition-all duration-300 hover:border-secondary hover:bg-secondary/5"
                  >
                    <span className="absolute inset-0 bg-secondary/5 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                    <span className="relative z-10 inline-flex items-center gap-1.5 group-hover:text-secondary transition-colors duration-300">
                      {Icon(FiDownload, 'text-xs')}
                      Download CV
                    </span>
                  </motion.button>

                  <motion.a
                    href="#contact"
                    whileHover={{ scale: 1.04 }}
                    whileTap={{ scale: 0.97 }}
                    className="group relative px-5 py-2.5 border-2 border-textPrimary/20 dark:border-border/60 rounded-lg text-xs font-medium text-textPrimary overflow-hidden transition-all duration-300 hover:border-secondary hover:bg-secondary/5"
                  >
                    <span className="absolute inset-0 bg-secondary/5 -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                    <span className="relative z-10 group-hover:text-secondary transition-colors duration-300">
                      Reach Me
                    </span>
                  </motion.a>
                </motion.div>

                {/* Social Icons — no background, larger, with tooltips */}
                <motion.div
                  variants={itemVariants}
                  className="flex items-center gap-5 mt-8"
                >
                  {socialLinks.map((link) => (
                    <motion.a
                      key={link.label}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ y: -3, scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                      className={`relative text-textSecondary/50 transition-colors duration-300 ${link.color}`}
                      aria-label={link.label}
                    >
                      {Icon(link.icon, 'text-xl')}
                    </motion.a>
                  ))}
                </motion.div>
              </motion.div>
            </div>
          </div>

          {/* ═══════════════ MOBILE: Profile Image ═══════════════ */}
          <div className="lg:hidden flex justify-center items-center py-6 px-6">
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative group"
            >
              <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-60 transition-opacity duration-500" />
              <div className="relative overflow-hidden rounded-2xl">
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent z-10 pointer-events-none" />
                <img
                  src="/images/computer.jpeg"
                  alt="Evode MUYISINGIZE"
                  className="w-48 h-64 sm:w-56 sm:h-72 object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-secondary rounded-full opacity-70" />
            </motion.div>
          </div>

          {/* ═══════════════ DESKTOP: Profile + Decorations ═══════════════ */}
          <div className="hidden lg:flex w-1/2 items-center justify-center relative">
            {/* Profile Image with Glow Ring */}
            <motion.div
              initial={{ opacity: 0, x: 40, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative group"
            >
              {/* Outer glow ring */}
              <div className="absolute -inset-4 rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

              {/* Animated border ring */}
              <div className="absolute -inset-[3px] rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-60 transition-opacity duration-500" />

              {/* Corner accents */}
              <div className="absolute -top-3 -left-3 w-8 h-8 border-t-2 border-l-2 border-secondary/30 rounded-tl transition-all duration-500 group-hover:w-10 group-hover:h-10 group-hover:border-secondary/70" />
              <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-2 border-r-2 border-secondary/30 rounded-br transition-all duration-500 group-hover:w-10 group-hover:h-10 group-hover:border-secondary/70" />

              {/* Image container */}
              <div className="relative overflow-hidden rounded-2xl transition-transform duration-500 group-hover:scale-[1.02]">
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent z-10 pointer-events-none" />
                <img
                  src="/images/computer.jpeg"
                  alt="Evode MUYISINGIZE"
                  className="w-72 h-96 md:w-80 md:h-[420px] lg:w-[340px] lg:h-[450px] object-cover"
                />
              </div>

              {/* Accent dot */}
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-secondary rounded-full opacity-70 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
            </motion.div>

            {/* Floating Tech Icons (no backgrounds) */}
            {floatingIcons.map(({ Icon: FloIcon, className, size, x, y, delay }, i) => (
              <motion.div
                key={i}
                className={`absolute floating-icon ${className}`}
                style={{
                  left: '50%',
                  top: '50%',
                  marginLeft: x,
                  marginTop: y,
                  animationDelay: `${delay}s`,
                }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + delay * 0.3 }}
              >
                <span style={{ fontSize: size, display: 'flex' }}>
                  {Icon(FloIcon)}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Scroll Indicator (centered overlay) ── */}
        <motion.a
          href="#about"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20 cursor-pointer group"
        >
          <span className="text-[10px] font-mono text-textSecondary/50 tracking-[0.25em] group-hover:text-textSecondary/80 transition-colors">
            SCROLL
          </span>
          <div className="w-6 h-10 border-2 border-border/40 rounded-full flex justify-center pt-2 group-hover:border-secondary/60 transition-colors">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1.5 h-2.5 bg-secondary rounded-full"
            />
          </div>
        </motion.a>
      </section>

      <MyCv isOpen={isCvOpen} onClose={() => setIsCvOpen(false)} />
    </>
  );
};

export default Hero;
